import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle, STATUS_COLOR } from "./_roadsCommon";

/**
 * Ch 21.1 — Horizontal Alignment Editor.
 * Plan-view SVG canvas with click-to-place PIs, draggable vertices, and
 * IRC 73 design-speed compliance dashboard.
 */
export default function AlignmentEditorPanel({ projectId }) {
  const [alignments, setAlignments] = useState([]);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("select"); // select | add-pi
  const [err, setErr] = useState("");
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const loadAlignments = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/roads/alignments`);
      setAlignments(data.alignments || []);
      if (!active && data.alignments?.[0]) setActive(data.alignments[0]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  useEffect(() => { loadAlignments(); /* eslint-disable-next-line */ }, []);

  const createNew = async () => {
    const name = window.prompt("Alignment name:", `Mainline ${alignments.length + 1}`);
    if (!name) return;
    const speed = parseFloat(window.prompt("Design speed (km/h):", "80")) || 80;
    try {
      const { data } = await api.post(`/projects/${projectId}/roads/alignments`,
        { name, design_speed_kmh: speed,
          pis: [{ id: "PI-1", x: 0, y: 0 }, { id: "PI-2", x: 1000, y: 0 }] });
      setActive(data.alignment);
      loadAlignments();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const updatePIs = async (newPIs) => {
    if (!active) return;
    try {
      const { data } = await api.put(
        `/projects/${projectId}/roads/alignments/${active.id}`,
        { pis: newPIs });
      setActive(data.alignment);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const onCanvasClick = (e) => {
    if (mode !== "add-pi" || !active) return;
    const pt = svgPoint(e, svgRef.current, viewBox);
    const newPIs = [...active.pis, { id: `PI-${active.pis.length + 1}`,
                                          x: Math.round(pt.x), y: Math.round(pt.y) }];
    updatePIs(newPIs);
    setMode("select");
  };

  const startDrag = (idx, e) => {
    e.stopPropagation();
    dragRef.current = { idx, startX: e.clientX, startY: e.clientY,
                         original: active.pis[idx] };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };
  const onDragMove = (e) => {
    if (!dragRef.current || !active) return;
    const pt = svgPoint(e, svgRef.current, viewBoxRef.current);
    const newPIs = active.pis.map((p, i) =>
      i === dragRef.current.idx ? { ...p, x: Math.round(pt.x), y: Math.round(pt.y) } : p);
    setActive({ ...active, pis: newPIs });
  };
  const onDragEnd = () => {
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
    if (active && dragRef.current) updatePIs(active.pis);
    dragRef.current = null;
  };

  const editPI = (idx, key, val) => {
    const newPIs = active.pis.map((p, i) =>
      i === idx ? { ...p, [key]: val === "" ? null : val } : p);
    setActive({ ...active, pis: newPIs });
  };

  const deletePI = (idx) => {
    if (active.pis.length <= 2) return alert("Alignment requires at least 2 PIs.");
    if (!window.confirm(`Delete ${active.pis[idx].id}?`)) return;
    const newPIs = active.pis.filter((_, i) => i !== idx);
    updatePIs(newPIs);
  };

  // ── viewBox auto-fit ─────────────────────────────────────
  const viewBox = useMemo(() => {
    if (!active?.pis?.length) return "0 0 1000 1000";
    const xs = active.pis.map((p) => p.x), ys = active.pis.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w = (maxX - minX) || 1000, h = (maxY - minY) || 1000;
    const pad = Math.max(w, h) * 0.15 + 100;
    return `${minX - pad} ${-(maxY + pad)} ${w + 2 * pad} ${h + 2 * pad}`;
  }, [active?.pis]);
  const viewBoxRef = useRef(viewBox); viewBoxRef.current = viewBox;

  if (!alignments.length && !active) {
    return (
      <div data-testid="alignment-editor" style={{ padding: 24 }}>
        <div style={{ ...label, marginBottom: 8 }}>Wave F · Ch 21.1 — Road Alignment Editor</div>
        <p style={{ fontSize: 12, color: "var(--pf-muted)", marginBottom: 16 }}>
          No horizontal alignments yet. Create one to begin designing the road centerline.
        </p>
        <button data-testid="alignment-new" style={btn} onClick={createNew}>+ New Alignment</button>
        {err && <div style={{ marginTop: 12, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    );
  }

  const checks = active?.geometry?.checks || [];
  const total  = active?.geometry?.total_length_m || 0;
  const segs   = active?.geometry?.segments || [];

  return (
    <div data-testid="alignment-editor" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* LEFT — PI table */}
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
          <select data-testid="alignment-select" style={inputStyle}
            value={active?.id || ""} onChange={(e) =>
              setActive(alignments.find((a) => a.id === e.target.value) || null)}>
            {alignments.map((a) => (
              <option key={a.id} value={a.id}>{a.name} · {a.design_speed_kmh} km/h</option>
            ))}
          </select>
          <button data-testid="alignment-new" style={btnGhost} onClick={createNew}>+</button>
        </div>
        <div style={{ ...label, marginBottom: 4 }}>PI Table</div>
        <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              <th style={{ textAlign: "left", padding: 3 }}>PI</th>
              <th style={{ textAlign: "right", padding: 3 }}>X</th>
              <th style={{ textAlign: "right", padding: 3 }}>Y</th>
              <th style={{ textAlign: "right", padding: 3 }}>R (m)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(active?.pis || []).map((p, i) => (
              <tr key={p.id} data-testid={`alignment-pi-${p.id}`}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <td style={{ padding: 3, fontFamily: "monospace" }}>{p.id}</td>
                <td><input style={{ ...inputStyle, padding: 1, fontSize: 10 }} type="number"
                              value={p.x} onChange={(e) => editPI(i, "x", parseFloat(e.target.value))}
                              onBlur={() => updatePIs(active.pis)} /></td>
                <td><input style={{ ...inputStyle, padding: 1, fontSize: 10 }} type="number"
                              value={p.y} onChange={(e) => editPI(i, "y", parseFloat(e.target.value))}
                              onBlur={() => updatePIs(active.pis)} /></td>
                <td><input style={{ ...inputStyle, padding: 1, fontSize: 10, width: 50 }} type="number"
                              value={p.radius_m || ""} placeholder="–"
                              onChange={(e) => editPI(i, "radius_m", parseFloat(e.target.value))}
                              onBlur={() => updatePIs(active.pis)} /></td>
                <td><button data-testid={`alignment-pi-del-${p.id}`}
                              style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
                              onClick={() => deletePI(i)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ ...label, marginTop: 12 }}>Total length</div>
        <div style={{ fontSize: 14, color: "#F26B1F", fontFamily: "monospace" }}>
          {(total / 1000).toFixed(3)} km
        </div>
      </div>

      {/* CENTER — Plan-view canvas */}
      <div style={{ ...card, padding: 0, overflow: "hidden", position: "relative" }}>
        <div style={{ display: "flex", gap: 6, padding: 6, background: "var(--pf-surface-2)",
                       borderBottom: "1px solid var(--pf-border)" }}>
          <button data-testid="alignment-add-pi-mode"
            style={mode === "add-pi" ? btn : btnGhost}
            onClick={() => setMode(mode === "add-pi" ? "select" : "add-pi")}>
            {mode === "add-pi" ? "Click canvas to place PI…" : "+ Add PI"}
          </button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Drag any PI to move it · click "+ Add PI" then click the canvas
          </span>
        </div>
        <svg ref={svgRef} viewBox={viewBox} data-testid="alignment-canvas"
              onClick={onCanvasClick}
              style={{ width: "100%", height: "calc(100% - 36px)", background: "#0a1220",
                       cursor: mode === "add-pi" ? "crosshair" : "default" }}>
          {/* Grid */}
          <defs>
            <pattern id="aGrid" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#1a2845" strokeWidth="1" />
            </pattern>
          </defs>
          <rect x={-1e6} y={-1e6} width={2e6} height={2e6} fill="url(#aGrid)" />

          {/* Centerline */}
          {segs.map((s, i) => {
            if (s.type === "tangent") {
              return <line key={i} x1={s.from[0]} y1={-s.from[1]} x2={s.to[0]} y2={-s.to[1]}
                              stroke="#F26B1F" strokeWidth={3} />;
            }
            if (s.type === "circular") {
              const sweepFlag = ((s.to_bearing_deg - s.from_bearing_deg + 540) % 360 - 180) > 0 ? 1 : 0;
              const largeArc = s.deflection_deg > 180 ? 1 : 0;
              return <path key={i} d={`M ${s.PC[0]} ${-s.PC[1]} A ${s.radius_m} ${s.radius_m} 0 ${largeArc} ${sweepFlag} ${s.PT[0]} ${-s.PT[1]}`}
                              fill="none" stroke="#3aa0ff" strokeWidth={3} strokeDasharray="6,3" />;
            }
            return null;
          })}

          {/* PIs */}
          {(active?.pis || []).map((p, i) => (
            <g key={p.id} transform={`translate(${p.x},${-p.y})`}
                onMouseDown={(e) => startDrag(i, e)} style={{ cursor: "move" }}
                data-testid={`alignment-marker-${p.id}`}>
              <circle r={20} fill="#F26B1F" stroke="#fff" strokeWidth={2} />
              <text y={5} fontSize={20} textAnchor="middle" fill="#0e0e10"
                     fontFamily="monospace" fontWeight={700}>{i + 1}</text>
              <text y={-30} fontSize={16} textAnchor="middle" fill="#fff"
                     fontFamily="monospace">{p.id}</text>
            </g>
          ))}
        </svg>
      </div>

      {/* RIGHT — Design checks */}
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Design checks · IRC 73</div>
        <div style={{ fontSize: 10, color: "var(--pf-muted)", marginBottom: 4 }}>
          Speed: <b style={{ color: "#F26B1F" }}>{active?.design_speed_kmh} km/h</b>
        </div>
        <div data-testid="alignment-checks" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {checks.length === 0 && (
            <div style={{ fontSize: 10, color: "#22c55e" }}>✓ No curves yet — alignment is all-tangent</div>
          )}
          {checks.map((c, i) => (
            <div key={i} style={{ padding: 6, fontSize: 10, borderRadius: 4,
                background: STATUS_COLOR[c.radius_status] + "22",
                borderLeft: `3px solid ${STATUS_COLOR[c.radius_status]}` }}>
              <div><b>{c.pi}</b> · R={c.radius_m}m · Δ={c.deflection_deg}°</div>
              <div style={{ color: STATUS_COLOR[c.radius_status], fontWeight: 600 }}>
                {c.radius_status} (Rmin ruling = {c.Rmin_ruling_m}m)
              </div>
              <div>e = {(c.superelevation_required * 100).toFixed(2)}% · SSD = {c.ssd_m}m · Spiral Ls = {Math.round(c.spiral_l_m)}m</div>
            </div>
          ))}
        </div>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function svgPoint(evt, svg, viewBox) {
  const rect = svg.getBoundingClientRect();
  const [vx, vy, vw, vh] = (viewBox || "0 0 1000 1000").split(" ").map(Number);
  const xRatio = (evt.clientX - rect.left) / rect.width;
  const yRatio = (evt.clientY - rect.top) / rect.height;
  return { x: vx + xRatio * vw, y: -(vy + yRatio * vh) };
}
